import { useCallback, useState } from 'react';
import type { SetURLSearchParams } from 'react-router-dom';
import type { IncidentGroupPatternActionMode } from '../../components/incidents/IncidentGroupPatternActionDialog';
import type { Incident, IncidentGroup } from '../../services/incidentsApi';
import { incidentsApi } from '../../services/incidentsApi';
import { buildIncidentReviewDeepLinkLoadKey } from './incidentReviewDeepLink';
import { resolveHeatmapOccurrenceWindow, type HeatmapOccurrenceWindow } from './resolveHeatmapOccurrenceWindow';
import type { IncidentTimeRange } from './useIncidentsTimeRange';

const TRANSIENT_NETWORK_RE = /ERR_NETWORK_CHANGED|Network Error|Failed to fetch/i;

async function withTransientNetworkRetry<T>(fn: () => Promise<T>, attempts = 3): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;
      const message = String(error?.message || error?.code || '');
      if (!TRANSIENT_NETWORK_RE.test(message) || attempt >= attempts - 1) {
        throw error;
      }
      await new Promise((resolve) => window.setTimeout(resolve, 350 * (attempt + 1)));
    }
  }
  throw lastError;
}

export type GroupedHeatmapCellSelectArgs = {
  dimension: 'zone' | 'camera';
  id: string;
  hour: number;
};

export type UseGroupedPatternReviewActionsArgs = {
  incidents: Incident[] | null | undefined;
  selectedIncident: Incident | null | undefined;
  setSelectedIncident: (incident: Incident) => void;
  selectedGroupContext: IncidentGroup | null | undefined;
  timeRange: IncidentTimeRange;
  heatmapWindow?: Partial<HeatmapOccurrenceWindow> | null;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  setError: (message: string | null) => void;
  setHeatmapViewFeedback?: (message: string | null) => void;
  onHeatmapOccurrencePinned?: (incidentId: string | null) => void;
  acknowledgeIncidentDeepLink?: (loadKey: string) => void;
  setFilterHeatmapHour: (hour: number) => void;
  handleOpenIncidentReviewPage: (incidentId: string, options?: { exact?: boolean }) => void;
  fetchData: () => Promise<void>;
  setGroupPatternDialogGroup: (group: IncidentGroup | null) => void;
  setGroupPatternDialogMode: (mode: IncidentGroupPatternActionMode) => void;
  setGroupPatternDialogOpen: (open: boolean) => void;
  setGroupPatternActionBusy: (busy: boolean) => void;
  canManageIncidentWorkflow: boolean;
  tableMode: string;
  reviewMode: boolean;
  onOccurrenceOpened?: (incidentId: string) => void;
};

export function useGroupedPatternReviewActions({
  selectedIncident,
  setSelectedIncident,
  selectedGroupContext,
  timeRange,
  heatmapWindow,
  searchParams,
  setSearchParams,
  setError,
  setHeatmapViewFeedback,
  onHeatmapOccurrencePinned,
  acknowledgeIncidentDeepLink,
  setFilterHeatmapHour,
  handleOpenIncidentReviewPage,
  fetchData,
  setGroupPatternDialogGroup,
  setGroupPatternDialogMode,
  setGroupPatternDialogOpen,
  setGroupPatternActionBusy,
  canManageIncidentWorkflow,
  tableMode,
  reviewMode,
  onOccurrenceOpened,
}: UseGroupedPatternReviewActionsArgs) {
  const [heatmapOpening, setHeatmapOpening] = useState(false);

  const handleGroupPatternAction = useCallback((group: IncidentGroup, mode: IncidentGroupPatternActionMode) => {
    setGroupPatternDialogGroup(group);
    setGroupPatternDialogMode(mode);
    setGroupPatternDialogOpen(true);
  }, [setGroupPatternDialogGroup, setGroupPatternDialogMode, setGroupPatternDialogOpen]);

  const handleGroupedHeatmapCellSelect = useCallback(async ({
    dimension,
    id,
    hour,
  }: GroupedHeatmapCellSelectArgs) => {
    const source: any = selectedGroupContext || selectedIncident;
    if (!source) return;

    const window = resolveHeatmapOccurrenceWindow(timeRange, heatmapWindow);
    if (!window) {
      setError('Select a review time window before opening an occurrence from the heatmap.');
      return;
    }

    const incidentType = String(source?.incident_type || '').trim();
    const zoneId =
      dimension === 'zone'
        ? (id === '__unassigned__' ? '__unassigned__' : id)
        : String(source?.zone_id || '').trim();
    const cameraId =
      dimension === 'camera'
        ? (id === '__unassigned__' ? '__unassigned__' : id)
        : String(source?.camera_id || '').trim();
    const deviceId = String(source?.device_id || '').trim();

    setHeatmapOpening(true);
    try {
      const previousId = String(selectedIncident?.incident_id || '').trim();
      const target = await withTransientNetworkRetry(() =>
        incidentsApi.getIncidentGroupHeatmapOccurrence({
          dimension,
          id,
          hour,
          from_date: window.fromDate,
          to_date: window.toDate,
          incident_type: incidentType || undefined,
          zone_id: zoneId || undefined,
          device_id: deviceId || undefined,
          camera_id: cameraId || undefined,
        }),
      );
      const targetId = String(target?.incident_id || '').trim();
      if (!targetId) {
        setError(
          `No occurrence found for UTC hour ${String(hour).padStart(2, '0')}:00 in this recurring-risk window. Try widening the time range or use View risk occurrences.`,
        );
        return;
      }

      setSelectedIncident(target);
      onHeatmapOccurrencePinned?.(targetId);

      const next = new URLSearchParams(searchParams);
      next.set('heatmap_hour', String(hour));
      next.set('incident_id', targetId);
      if (reviewMode) {
        next.set('review', '1');
        next.delete('exact_incident');
        const deepLinkKey = buildIncidentReviewDeepLinkLoadKey({
          reviewMode: true,
          preserveExactOccurrence: true,
          incidentId: targetId,
          fromDate: timeRange.fromDate,
          toDate: timeRange.toDate,
          heatmapHour: hour,
        });
        acknowledgeIncidentDeepLink?.(deepLinkKey);
        setSearchParams(next, { replace: true });
      } else {
        setFilterHeatmapHour(hour);
        setSearchParams(next, { replace: true });
      }

      const openedMessage =
        targetId === previousId
          ? `Opened UTC hour ${String(hour).padStart(2, '0')}:00 — representative evidence ${targetId} is the newest match in this window. The evidence review below was refreshed.`
          : `Opened observation ${targetId} for UTC hour ${String(hour).padStart(2, '0')}:00. Evidence and audit below now reflect that observation.`;
      if (typeof setHeatmapViewFeedback === 'function') {
        setHeatmapViewFeedback(openedMessage);
      } else {
        setError(openedMessage);
      }

      if (reviewMode) {
        onOccurrenceOpened?.(targetId);
      } else {
        handleOpenIncidentReviewPage(targetId, { exact: true });
      }
    } catch (error: any) {
      setError(error?.response?.data?.error || error?.message || 'Failed to load occurrences for that hour');
    } finally {
      setHeatmapOpening(false);
    }
  }, [
    handleOpenIncidentReviewPage,
    heatmapWindow,
    onOccurrenceOpened,
    reviewMode,
    searchParams,
    selectedGroupContext,
    selectedIncident,
    acknowledgeIncidentDeepLink,
    onHeatmapOccurrencePinned,
    setError,
    setHeatmapViewFeedback,
    setFilterHeatmapHour,
    setSearchParams,
    setSelectedIncident,
    timeRange,
  ]);

  const handleGroupPatternActionCompleted = useCallback(async () => {
    setGroupPatternActionBusy(true);
    try {
      await fetchData();
    } finally {
      setGroupPatternActionBusy(false);
    }
  }, [fetchData, setGroupPatternActionBusy]);

  const canManageGroupPatterns =
    canManageIncidentWorkflow && (tableMode === 'grouped' || (reviewMode && Boolean(selectedGroupContext)));

  return {
    handleGroupPatternAction,
    handleGroupedHeatmapCellSelect,
    handleGroupPatternActionCompleted,
    canManageGroupPatterns,
    heatmapOpening,
  };
}
